import { Injectable } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/functions';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class StripeService {
  constructor(private functions: AngularFireFunctions) { }

  // Mark invoice as paid
  public async markInvoiceAsPaid(invoiceId: string): Promise<any> {
    const markInvoiceAsPaid = this.functions.httpsCallable('markInvoiceAsPaid');
    return markInvoiceAsPaid({ invoiceId }).toPromise();
  }

  // Add credit balance to customer
  public async addCreditBalance(customerId: string, amount: number, currency: string = 'usd', description: string = 'Credit balance added'): Promise<any> {
    const addCreditBalance = this.functions.httpsCallable('addCreditBalance');
    return addCreditBalance({ customerId, amount, currency, description }).toPromise();
  }

  // Add credit balance alternative method
  public async addCreditBalanceAlternative(customerId: string, amount: number, currency: string = 'usd', description: string = 'Credit balance added'): Promise<any> {
    // For now, just call the same method - the fallback logic is handled in the component
    return this.addCreditBalance(customerId, amount, currency, description);
  }

  // Get customer invoices
  public async getCustomerInvoices(customerId: string): Promise<any> {
    const getCustomerInvoices = this.functions.httpsCallable('getCustomerInvoices');
    return getCustomerInvoices({ customerId }).toPromise();
  }

  // Get last unpaid invoice for customer
  public async getLastInvoice(customerId: string): Promise<any> {
    const getLastInvoice = this.functions.httpsCallable('getLastInvoice');
    return getLastInvoice({ customerId }).toPromise();
  }

  // Get customer credit balance
  public async getCustomerCreditBalance(customerId: string): Promise<any> {
    const getCustomerCreditBalance = this.functions.httpsCallable('getCustomerCreditBalance');
    return getCustomerCreditBalance({ customerId }).toPromise();
  }

  // Void invoice
  public async voidInvoice(invoiceId: string): Promise<any> {
    const voidInvoice = this.functions.httpsCallable('voidInvoice');
    return voidInvoice({ invoiceId }).toPromise();
  }

  // Registration flow methods
  public async createCustomer(email: string, name: string, source = ''): Promise<any> {
    const createCustomer = this.functions.httpsCallable('createCustomer');
    return createCustomer({ email, name, source }).toPromise();
  }

  public async createSubscription(customer: string, plan: string, cupon: string, trial_end = '', proration_behavior = 'none'): Promise<any> {
    const createSubscription = this.functions.httpsCallable('createSubscription');
    return createSubscription({ customer, plan, cupon, trial_end, proration_behavior }).toPromise();
  }

  public async deleteCustomer(customerId: string): Promise<any> {
    const deleteCustomer = this.functions.httpsCallable('deleteCustomer');
    return deleteCustomer({ customerId }).toPromise();
  }

  public async createFreeTrial(subscription: string, trialEnd: string): Promise<any> {
    const createFreeTrial = this.functions.httpsCallable('createFreeTrial');
    return createFreeTrial({ subscription, trialEnd }).toPromise();
  }

  public async getSubscription(subscriptionId: string): Promise<any> {
    const getSubscription = this.functions.httpsCallable('getSubscription');
    return getSubscription({ subscriptionId }).toPromise();
  }

  public async getOldSubscription(id: string): Promise<any> {
    const getOldSubscription = this.functions.httpsCallable('getOldSubscription');
    return getOldSubscription({ id }).toPromise();
  }

  public async validateCoupon(id: string): Promise<any> {
    const validateCoupon = this.functions.httpsCallable('validateCoupon');
    return validateCoupon({ id }).toPromise();
  }

  public async cancelAutomaticRenewal(subscription: string): Promise<any> {
    const cancelAutomaticRenewal = this.functions.httpsCallable('cancelAutomaticRenewal');
    return cancelAutomaticRenewal({ subscription }).toPromise();
  }

  public async activateAutomaticRenewal(subscription: string): Promise<any> {
    const activateAutomaticRenewal = this.functions.httpsCallable('activateAutomaticRenewal');
    return activateAutomaticRenewal({ subscription }).toPromise();
  }

  public async getCoupons(): Promise<any> {
    const getCoupons = this.functions.httpsCallable('getCoupons');
    return getCoupons({}).toPromise();
  }

  public async createCoupon(id: string, name: string, amount_off: number): Promise<any> {
    const createCoupon = this.functions.httpsCallable('createCoupon');
    return createCoupon({ id, name, amount_off }).toPromise();
  }

  public async customerPortal(customer: string, return_url: string): Promise<any> {
    const customerPortal = this.functions.httpsCallable('customerPortal');
    return customerPortal({ customer, return_url }).toPromise();
  }

  public async getCustomer(customerId: string): Promise<any> {
    const getCustomer = this.functions.httpsCallable('getCustomer');
    return getCustomer({ customerId }).toPromise();
  }

  public async addCardToCustomer(customer: string, token: string): Promise<any> {
    const addCardToCustomer = this.functions.httpsCallable('addCardToCustomer');
    return addCardToCustomer({ customer, token }).toPromise();
  }

  public async createCustomerCredit(customerId: string, amount: number, currency: string = 'usd', description: string = 'Credit balance added'): Promise<any> {
    return this.addCreditBalance(customerId, amount, currency, description);
  }
}